// Helpers dùng chung cho module Warranty Service / Phiếu sửa chữa (FE).
// Tránh duplicate giữa RepairSlipForm / ListRepairSlips / MasterRepairInfos / MasterRepairErrorCodes.

import moment from 'moment';

/**
 * Trích message lỗi từ axios error → string user-facing.
 * @param {*} err
 * @returns {string}
 */
export const extractError = (err) => (
    err?.response?.data?.message
    || err?.message
    || 'Có lỗi xảy ra, vui lòng thử lại'
);

/**
 * Format số tiền VND theo locale vi-VN.
 * @param {number|string|null|undefined} value
 * @returns {string}
 */
export const formatPrice = (value) => {
    if (value === undefined || value === null) return '0';
    return Number(value).toLocaleString('vi-VN');
};

/**
 * Convert input từ date-picker (DD/MM/YYYY hoặc ISO) sang ISO YYYY-MM-DD cho BE.
 * @param {string|Date|null|undefined} input
 * @returns {string|null}
 */
export const toIsoDate = (input) => {
    if (!input) return null;
    if (input instanceof Date) {
        return moment(input).format('YYYY-MM-DD');
    }
    const m = moment(input, ['DD/MM/YYYY', 'YYYY-MM-DD', moment.ISO_8601], true);
    return m.isValid() ? m.format('YYYY-MM-DD') : null;
};

/**
 * Convert ISO YYYY-MM-DD từ BE → DD/MM/YYYY cho date-picker hiển thị.
 * @param {string|Date|null|undefined} input
 * @returns {string}
 */
export const fromIsoDate = (input) => {
    if (!input) return '';
    const m = moment(input);
    return m.isValid() ? m.format('DD/MM/YYYY') : '';
};

/**
 * Format datetime ISO → DD/MM/YYYY HH:mm[:ss].
 * @param {string|Date|null|undefined} value
 * @param {boolean} [withSeconds=false]
 * @returns {string}
 */
export const formatDateTime = (value, withSeconds = false) => {
    if (!value) return '';
    return moment(value).format(withSeconds ? 'DD/MM/YYYY HH:mm:ss' : 'DD/MM/YYYY HH:mm');
};

// ============================================================
// "Hoá đơn dịch vụ" in cho khách — tính tiền dùng chung giữa popup tick
// (RepairSlipForm) và bản in (PrintRepairBill). Một source of truth (DRY).
// ============================================================

/**
 * Gom productId + productCode của các dòng "không tính phí KH" để map sang billItems.
 * @param {Array<{productId?:string, productCode?:string}>} noChargeParts — đã lọc sẵn dòng miễn phí
 * @returns {{ids:Set<string>, codes:Set<string>}}
 */
export const buildNoChargeKeys = (noChargeParts) => {
    const ids = new Set();
    const codes = new Set();
    (noChargeParts || []).forEach((p) => {
        if (p.productId) ids.add(String(p.productId));
        if (p.productCode) codes.add(String(p.productCode));
    });
    return { ids, codes };
};

/**
 * 1 billItem có thuộc diện "không tính phí KH" không (match theo productId, fallback productCode).
 * @param {Object} item — billItem
 * @param {{ids:Set<string>, codes:Set<string>}} keys
 * @returns {boolean}
 */
export const isItemNoCharge = (item, keys) => !!(
    (item.productId && keys.ids.has(String(item.productId)))
    || (item.productCode && keys.codes.has(String(item.productCode)))
);

/**
 * Tính tiền 1 dòng billItem cho bill dịch vụ in cho khách.
 * CHÚ Ý: BE `totalPrice` = qty × productPrice (GROSS, trước CK) → tự tính amount = gross - CK.
 * Dòng "không tính phí KH" (noCharge=true) → CK 100%, amount = 0.
 * @param {Object} it — billItem
 * @param {boolean} noCharge
 * @returns {{quantity:number, unitPrice:number, lineDiscount:number, amount:number}}
 */
export const computeServiceBillLine = (it, noCharge) => {
    const quantity = Number(it.quantity != null ? it.quantity : it.qty) || 1;
    const unitPriceRaw = it.productPrice || it.price || it.sellingPrice;
    let unitPrice = Number(unitPriceRaw) || 0;
    const discountRaw = Number(it.discountAmount) || 0;
    const isPercent = String(it.discountType || '') === '2';
    // Fallback Đơn giá khi BE không trả productPrice nhưng có totalPrice (gross).
    if (unitPrice === 0 && quantity > 0) {
        const grossFromBE = Number(it.totalPrice) || 0;
        if (grossFromBE > 0) unitPrice = Math.round(grossFromBE / quantity);
    }
    const gross = quantity * unitPrice;
    const lineDiscount = isPercent ? Math.round((gross * discountRaw) / 100) : discountRaw;
    const amount = noCharge ? 0 : Math.max(0, gross - lineDiscount);
    return { quantity, unitPrice, lineDiscount, amount };
};

/**
 * Chiết khấu mức hoá đơn (đã quy ra tiền): ưu tiên discountValue, fallback discountAmount.
 * @param {Object|null} bill
 * @returns {number}
 */
export const getServiceBillDiscount = (bill) => {
    if (!bill) return 0;
    const v = Number(bill.discountValue);
    if (v > 0) return v;
    return Number(bill.discountAmount) || 0;
};

/**
 * Tổng "Khách phải trả" = Σ amount các dòng (đã zero dòng miễn phí) − CK mức hoá đơn.
 * @param {Array} billItems
 * @param {{ids:Set<string>, codes:Set<string>}} noChargeKeys
 * @param {Object|null} bill — để lấy CK mức hoá đơn
 * @returns {number}
 */
export const computeCustomerPayable = (billItems, noChargeKeys, bill) => {
    const subtotal = (billItems || []).reduce((sum, it) => {
        const noCharge = isItemNoCharge(it, noChargeKeys);
        return sum + computeServiceBillLine(it, noCharge).amount;
    }, 0);
    return Math.max(0, subtotal - getServiceBillDiscount(bill));
};
